/**
 * S-27 › Discovery filters (V7 — #19/#20/#21/#22/#23/#26).
 *
 * GET/PATCH /discovery/filters. Basics (#19 age, #20 distance, #21 show-me)
 * are free; verified-only is free; advanced vitals (#22) carry the normative
 * free-tier cap — ANY two simultaneously-active filters of the user's own
 * choosing, changeable anytime; enabling a 3rd opens the paywall sheet, and
 * the server enforces the cap regardless (402 PREMIUM_REQUIRED → same sheet).
 * Dealbreakers (#23): hard excludes, soft ranks; on age/distance the
 * dealbreaker rides a meta row (values stay empty — the base filter is
 * always on, `hard` adds the reverse direction). Passport (#26) is the
 * premium upsell surface.
 *
 * The client never decides entitlement: `premium` and `free_cap` arrive from
 * the server per request and every save is re-validated at write time.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GENDERS, resolveCopy } from "@humble/shared";
import type { ApiComponents } from "@humble/shared";
import { Button, Card, rise, Sheet, Skeleton, stagger, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import "./filters.css";

type FiltersView = ApiComponents["schemas"]["FiltersView"];
type Dealbreaker = "none" | "soft" | "hard";
type Gender = (typeof GENDERS)[number];

/**
 * Client mirror of the server vitals allowlist (apps/server/src/sm/deck.ts
 * VITALS_COLUMNS) — an unknown field is a 400, so this list must only ever
 * shrink relative to the server's.
 */
const VITALS: ReadonlyArray<{ field: string; label: string; hint: string }> = [
  { field: "height", label: "Height", hint: "e.g. 170, 180" },
  { field: "job", label: "Job", hint: "e.g. nurse, firefighter" },
  { field: "education", label: "Education", hint: "e.g. masters" },
  { field: "drinking", label: "Drinking", hint: "e.g. never, socially" },
  { field: "smoking", label: "Smoking", hint: "e.g. never" },
  { field: "kids", label: "Kids", hint: "e.g. none, someday" },
  { field: "religion", label: "Religion", hint: "e.g. none" },
  { field: "politics", label: "Politics", hint: "e.g. moderate" },
  { field: "zodiac", label: "Zodiac", hint: "e.g. scorpio" },
  { field: "relationship_intent", label: "Intent", hint: "e.g. long_term" },
];

const GENDER_LABELS: Record<Gender, string> = {
  woman: "Women",
  man: "Men",
  nonbinary: "Nonbinary people",
};

const DEALBREAKERS: ReadonlyArray<{ value: Dealbreaker; label: string }> = [
  { value: "none", label: "Off" },
  { value: "soft", label: "Soft" },
  { value: "hard", label: "Hard" },
];

interface VitalsRow {
  field: string;
  enabled: boolean;
  /** Raw comma-separated editing text; parsed on save. */
  text: string;
  dealbreaker: Dealbreaker;
}

function parseValues(text: string): string[] {
  return [
    ...new Set(
      text
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => s.slice(0, 60)),
    ),
  ].slice(0, 20);
}

function initRows(v: FiltersView): VitalsRow[] {
  return VITALS.map(({ field }) => {
    const cur = v.vitals_filters.find((f) => f.field === field);
    const values = cur?.values ?? [];
    return {
      field,
      enabled: values.length > 0,
      text: values.join(", "),
      dealbreaker: cur?.dealbreaker ?? "none",
    };
  });
}

function metaDealbreaker(v: FiltersView, field: "age" | "distance"): Dealbreaker {
  return v.vitals_filters.find((f) => f.field === field)?.dealbreaker ?? "none";
}

export function DiscoveryFiltersScreen() {
  const [view, setView] = useState<FiltersView | null>(null);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(99);
  const [distanceKm, setDistanceKm] = useState(50);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [showMe, setShowMe] = useState<Gender[]>([]);
  const [rows, setRows] = useState<VitalsRow[]>([]);
  const [ageDb, setAgeDb] = useState<Dealbreaker>("none");
  const [distanceDb, setDistanceDb] = useState<Dealbreaker>("none");
  const [paywall, setPaywall] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyView = useCallback((v: FiltersView) => {
    setView(v);
    setAgeMin(v.age_range.min);
    setAgeMax(v.age_range.max);
    setDistanceKm(v.distance_km);
    setVerifiedOnly(v.verified_only);
    setShowMe((v.show_me ?? []) as Gender[]);
    setRows(initRows(v));
    setAgeDb(metaDealbreaker(v, "age"));
    setDistanceDb(metaDealbreaker(v, "distance"));
  }, []);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const v = await api.get("/discovery/filters");
        if (alive) applyView(v);
      } catch (err) {
        if (isApiError(err)) toast(err.message, "error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [applyView]);

  if (view === null) {
    return (
      <div className="filters">
        <FiltersHead />
        <Skeleton height={180} radius={20} style={{ marginBottom: 12 }} />
        <Skeleton height={320} radius={20} />
      </div>
    );
  }

  const premium = view.premium;
  const freeCap = view.free_cap;
  const activeCount = rows.filter((r) => r.enabled && parseValues(r.text).length > 0).length;
  const enabledCount = rows.filter((r) => r.enabled).length;

  function toggleRow(field: string) {
    const row = rows.find((r) => r.field === field);
    if (!row) return;
    if (!row.enabled && !premium && enabledCount >= freeCap) {
      // #22: enabling one past the cap opens the paywall — nothing is applied.
      setPaywall(true);
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.field === field ? { ...r, enabled: !r.enabled } : r)),
    );
  }

  function patchRow(field: string, patch: Partial<VitalsRow>) {
    setRows((prev) => prev.map((r) => (r.field === field ? { ...r, ...patch } : r)));
  }

  function toggleGender(g: Gender) {
    setShowMe((prev) => {
      if (prev.includes(g)) {
        // #21: at least one audience always stays selected.
        return prev.length > 1 ? prev.filter((x) => x !== g) : prev;
      }
      return [...prev, g];
    });
  }

  async function save() {
    if (saving) return;
    const activeVitals = rows.filter((r) => r.enabled && parseValues(r.text).length > 0);
    if (!premium && activeVitals.length > freeCap) {
      setPaywall(true); // the server would 402 this anyway (#22)
      return;
    }
    setSaving(true);
    try {
      const min = Math.min(ageMin, ageMax);
      const max = Math.max(ageMin, ageMax);
      const v = await api.patch("/discovery/filters", {
        body: {
          age_range: { min, max },
          distance_km: distanceKm,
          verified_only: verifiedOnly,
          ...(showMe.length > 0 ? { show_me: showMe } : {}),
          vitals_filters: [
            ...rows
              .filter((r) => r.enabled)
              .map((r) => ({
                field: r.field,
                values: parseValues(r.text),
                dealbreaker: r.dealbreaker,
              })),
            { field: "age", values: [], dealbreaker: ageDb },
            { field: "distance", values: [], dealbreaker: distanceDb },
          ],
        },
      });
      applyView(v);
      toast(resolveCopy("filters.saved") ?? "Saved.", "success");
    } catch (err) {
      if (isApiError(err) && err.status === 402) setPaywall(true);
      else if (isApiError(err)) toast(err.message, "error");
      else toast(resolveCopy("errors.offline") ?? "Offline.", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div className="filters" variants={stagger} initial="initial" animate="enter">
      <motion.div variants={rise}>
        <FiltersHead />
      </motion.div>

      {/* #19 / #20 / #21 basics — free for everyone */}
      <motion.div variants={rise}>
      <Card padding="lg" style={{ marginBottom: 12 }}>
        <div className="field">
          <label htmlFor="age-min">Age range</label>
          <div className="filters__pair">
            <input
              id="age-min"
              type="number"
              min={18}
              max={99}
              value={ageMin}
              onChange={(e) => setAgeMin(Number(e.target.value) || 18)}
              aria-label="Minimum age"
            />
            <span className="text-faint">to</span>
            <input
              id="age-max"
              type="number"
              min={18}
              max={99}
              value={ageMax}
              onChange={(e) => setAgeMax(Number(e.target.value) || 99)}
              aria-label="Maximum age"
            />
          </div>
          <DealbreakerPicker
            id="db-age"
            label="Age dealbreaker"
            value={ageDb}
            onChange={setAgeDb}
          />
        </div>

        <div className="field">
          <label htmlFor="distance">Distance · {distanceKm} km</label>
          <input
            id="distance"
            type="range"
            min={1}
            max={500}
            value={distanceKm}
            onChange={(e) => setDistanceKm(Number(e.target.value))}
          />
          <DealbreakerPicker
            id="db-distance"
            label="Distance dealbreaker"
            value={distanceDb}
            onChange={setDistanceDb}
          />
        </div>

        <div className="field">
          <label>Show me</label>
          <div className="filters__chips" role="group" aria-label="Show me">
            {GENDERS.map((g) => (
              <label key={g} className={`filters__chip${showMe.includes(g) ? " filters__chip--on" : ""}`}>
                <input
                  type="checkbox"
                  checked={showMe.includes(g)}
                  onChange={() => toggleGender(g)}
                />
                {GENDER_LABELS[g]}
              </label>
            ))}
          </div>
        </div>

        <label className="filters__toggle">
          <input
            type="checkbox"
            checked={verifiedOnly}
            onChange={(e) => setVerifiedOnly(e.target.checked)}
          />
          Verified profiles only
        </label>
      </Card>
      </motion.div>

      {/* #22 advanced vitals + #23 dealbreakers */}
      <motion.div variants={rise}>
      <Card padding="lg" style={{ marginBottom: 12 }}>
        <div className="filters__rowhead">
          <h2 className="h-caps filters__h2">Advanced filters</h2>
          {!premium && (
            <span className="badge" aria-label={`${activeCount} of ${freeCap} free filters active`}>
              {activeCount}/{freeCap} active
            </span>
          )}
        </div>
        <p className="text-dim" style={{ marginTop: 0 }}>{resolveCopy("filters.vitals_cap_note")}</p>
        <p className="text-faint">{resolveCopy("filters.dealbreaker_hint")}</p>

        {VITALS.map(({ field, label, hint }) => {
          const row = rows.find((r) => r.field === field);
          if (!row) return null;
          return (
            <div key={field} className="filters__vital">
              <label className="filters__toggle">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={() => toggleRow(field)}
                  aria-label={`Filter by ${label}`}
                />
                {label}
              </label>
              {row.enabled && (
                <div className="filters__vitalbody">
                  <input
                    type="text"
                    value={row.text}
                    placeholder={`${hint} — comma separated`}
                    aria-label={`${label} values`}
                    onChange={(e) => patchRow(field, { text: e.target.value })}
                  />
                  <DealbreakerPicker
                    id={`db-${field}`}
                    label={`${label} dealbreaker`}
                    value={row.dealbreaker}
                    onChange={(d) => patchRow(field, { dealbreaker: d })}
                  />
                </div>
              )}
            </div>
          );
        })}
      </Card>
      </motion.div>

      {/* #26 Passport — premium-only; deck location override, base untouched */}
      <motion.div variants={rise}>
      <Card padding="lg" style={{ marginBottom: 12 }}>
        <div className="filters__rowhead">
          <h2 className="h-caps filters__h2">Passport</h2>
          {!premium && <span className="badge">Premium</span>}
        </div>
        <p className="text-dim" style={{ marginTop: 0 }}>
          Swipe from any city. Deck location only — your real location stays put.
        </p>
        {premium ? (
          <p className="text-faint" style={{ margin: 0 }}>
            Included with Premium. The city picker lands here.
          </p>
        ) : (
          <Button variant="ghost" onClick={() => setPaywall(true)}>
            Unlock with Premium
          </Button>
        )}
      </Card>
      </motion.div>

      <motion.div className="filters__save" variants={rise}>
        <Button variant="primary" block loading={saving} onClick={() => void save()}>
          Save filters
        </Button>
      </motion.div>

      {/* #22 paywall sheet — Premium, or disable one first */}
      <Sheet open={paywall} onClose={() => setPaywall(false)} title="Premium">
        <p style={{ marginTop: 0 }}>{resolveCopy("filters.vitals_cap_note")}</p>
        <p className="type-editorial text-dim" style={{ fontSize: "1.05rem" }}>
          {resolveCopy("store.premium_pitch")}
        </p>
        <Link to="/store" className="btn btn--primary btn--block">
          See Premium
        </Link>
      </Sheet>
    </motion.div>
  );
}

function FiltersHead() {
  return (
    <header className="filters__head">
      <p className="h-caps filters__kicker">The guest list</p>
      <h1 className="type-display-3">Discovery</h1>
      <p className="type-editorial filters__lede">{resolveCopy("filters.lede")}</p>
    </header>
  );
}

/** #23 dealbreaker — segmented Off / Soft / Hard (soft judges, hard removes). */
function DealbreakerPicker({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: Dealbreaker;
  onChange: (d: Dealbreaker) => void;
}) {
  return (
    <div className="filters__db" id={id} role="radiogroup" aria-label={label}>
      <span className="filters__db-label">Dealbreaker</span>
      <span className="filters__db-seg">
        {DEALBREAKERS.map((d) => (
          <button
            key={d.value}
            type="button"
            role="radio"
            aria-checked={value === d.value}
            className={`filters__db-opt filters__db-opt--${d.value}${
              value === d.value ? " filters__db-opt--on" : ""
            }`}
            onClick={() => onChange(d.value)}
          >
            {d.label}
          </button>
        ))}
      </span>
    </div>
  );
}
